/* GS://PROTOCOLO
 * Arquivo: src/components/forms/EditUserForm.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 2.2 – Edição de Contratos Financeiros
 * Impacto da Versão: [src/components/forms/EditUserForm.js]
 * Data: 16/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [ADD]: Campos para editar Remuneração (Comissão, Diária, PIX).
 * - [LOGIC]: Carrega os dados existentes do objeto `userToEdit.remuneration`.
 */

import React, { useState, useEffect } from 'react';
// Importação correta via serviço, não direta do SDK
import { updateUser } from '../../services/firestoreService';

// LISTA MESTRA DE PERMISSÕES
// Atualizada com os novos módulos industriais
const availablePermissions = [
  // Vendas e Loja
  { key: 'viewDashboard', label: 'Ver Dashboard' },
  { key: 'accessPOS', label: 'Acessar Frente de Caixa (PDV)' },
  { key: 'manageOrders', label: 'Gerenciar Pedidos' },
  { key: 'manageCustomers', label: 'Gerenciar Clientes' },
  { key: 'manageRentals', label: 'Gerenciar Aluguéis' },

  // Gestão de Produtos
  { key: 'manageProducts', label: 'Gerenciar Produtos (Comercial)' },
  { key: 'manageSuppliers', label: 'Gerenciar Fornecedores' },

  // Indústria e Estoque (NOVAS)
  { key: 'manage_stock', label: 'Acessar Estoque Técnico' },
  { key: 'manage_nfs', label: 'Entrada de Notas Fiscais' },
  { key: 'manage_assembly', label: 'Módulo de Montagem' },

  // Administrativo
  { key: 'managePurchases', label: 'Gerenciar Compras' },
  { key: 'accessFinanceiro', label: 'Acessar Financeiro' },
  { key: 'manageUsers', label: 'Gerenciar Usuários (RH)' },
];

const EditUserForm = ({ userToEdit, onFormSubmit, onCancel }) => {
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('vendedor');
  const [permissions, setPermissions] = useState({});
  
  // [GS-NEW]: Estados para Dados Financeiros
  const [remunerationType, setRemunerationType] = useState('comissao'); // comissao, diaria, hibrido
  const [dailyRate, setDailyRate] = useState(''); // Valor em R$
  const [commissionRate, setCommissionRate] = useState(''); // Valor em %
  const [pixKey, setPixKey] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (userToEdit) {
      setDisplayName(userToEdit.displayName || userToEdit.name || '');
      setRole(userToEdit.role || 'vendedor');
      
      // Carrega permissões existentes ou inicia como false
      const initialPerms = availablePermissions.reduce((acc, perm) => {
        acc[perm.key] = userToEdit.permissions?.[perm.key] || false;
        return acc;
      }, {});

      // Se já existiam permissões salvas no banco, elas têm prioridade
      if (userToEdit.permissions) {
          Object.keys(userToEdit.permissions).forEach(key => {
              initialPerms[key] = userToEdit.permissions[key];
          });
      }
      setPermissions(initialPerms);

      // [GS-NEW]: Carregar dados financeiros se existirem
      if (userToEdit.remuneration) {
          setRemunerationType(userToEdit.remuneration.type || 'comissao');
          setDailyRate(userToEdit.remuneration.dailyRate || '');
          setCommissionRate(userToEdit.remuneration.commissionRate || '');
          setPixKey(userToEdit.remuneration.pixKey || '');
      }
    }
  }, [userToEdit]);

  const handlePermissionChange = (e) => {
    const { name, checked } = e.target;
    setPermissions(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepara objeto de dados para envio
      const updateData = {
        displayName,
        role,
        permissions,
        // [GS-NEW]: Dados financeiros aninhados
        remuneration: {
            type: remunerationType,
            dailyRate: parseFloat(dailyRate) || 0,
            commissionRate: parseFloat(commissionRate) || 0,
            pixKey: pixKey
        }
      };

      // Chama a Cloud Function via serviço
      const result = await updateUser({
        uid: userToEdit.uid,
        data: updateData
      });
      
      setSuccess(result.data?.message || 'Usuário atualizado com sucesso!');
      
      setTimeout(() => { onFormSubmit(); }, 1500); 

    } catch (err) {
      console.error('Erro ao atualizar usuário:', err);
      setError(err.message || 'Erro desconhecido ao atualizar usuário.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!userToEdit) return null;

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-sm">
      {error && <div className="bg-red-50 text-red-700 p-3 rounded mb-4 border border-red-200">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 p-3 rounded mb-4 border border-green-200">{success}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="displayName">Nome Completo</label>
            <input
                id="displayName" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900 focus:ring-brand-gold focus:border-brand-gold" required
            />
        </div>

        <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="role">Papel (Função)</label>
            <select
                id="role" value={role} onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded text-gray-900 focus:ring-brand-gold focus:border-brand-gold" required >
                <option value="vendedor">Vendedor</option>
                <option value="atendente">Atendente</option>
                <option value="freelance">Freelance (Temporário)</option>
                <option value="estoquista">Estoquista (Industrial)</option>
                <option value="financeiro">Financeiro</option>
                <option value="gerente">Gerente</option>
                <option value="admin">Administrador (CEO)</option>
            </select>
        </div>
      </div>

      {/* [GS-NEW]: Seção de Dados Financeiros (Contratos) - Visual Idêntico ao AddUser */}
      <div className="mb-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <h3 className="text-sm font-bold text-yellow-800 mb-3 uppercase tracking-wide">Dados Financeiros (Contrato)</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <label className="block text-gray-700 text-xs font-bold mb-1" htmlFor="remunerationType">Modelo de Remuneração</label>
                <select
                    id="remunerationType"
                    value={remunerationType}
                    onChange={(e) => setRemunerationType(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold"
                >
                    <option value="comissao">Comissão Pura (Vendas)</option>
                    <option value="diaria">Diária Fixa</option>
                    <option value="hibrido">Híbrido (Diária + Comissão)</option>
                </select>
            </div>

            <div>
                <label className="block text-gray-700 text-xs font-bold mb-1" htmlFor="pixKey">Chave PIX (Para Pagamentos)</label>
                <input
                    id="pixKey"
                    type="text"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    placeholder="CPF, Email ou Aleatória"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold"
                />
            </div>

            {/* Renderização Condicional dos Valores */}
            {(remunerationType === 'diaria' || remunerationType === 'hibrido') && (
                <div>
                    <label className="block text-gray-700 text-xs font-bold mb-1" htmlFor="dailyRate">Valor da Diária (R$)</label>
                    <input
                        id="dailyRate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={dailyRate}
                        onChange={(e) => setDailyRate(e.target.value)}
                        placeholder="Ex: 80.00"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold"
                    />
                </div>
            )}

            {(remunerationType === 'comissao' || remunerationType === 'hibrido') && (
                <div>
                    <label className="block text-gray-700 text-xs font-bold mb-1" htmlFor="commissionRate">% de Comissão</label>
                    <input
                        id="commissionRate"
                        type="number"
                        min="0"
                        step="0.1"
                        value={commissionRate}
                        onChange={(e) => setCommissionRate(e.target.value)}
                        placeholder="Ex: 5.0"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-gold"
                    />
                </div>
            )}
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-3">Permissões de Acesso</label>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 bg-gray-50 p-4 rounded-lg border border-gray-200">
          {availablePermissions.map((perm) => (
            <label key={perm.key} className="flex items-start space-x-3 p-2 hover:bg-white rounded transition-colors cursor-pointer">
              <input
                type="checkbox" name={perm.key} checked={permissions[perm.key] || false} onChange={handlePermissionChange}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-gold focus:ring-brand-gold"
              />
              <span className="text-sm text-gray-700 leading-snug">{perm.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end space-x-4 border-t border-gray-100 pt-4">
        <button type="button" onClick={onCancel} className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded transition-colors">
          Cancelar
        </button>
        <button
          type="submit" disabled={isLoading}
          className="bg-brand-gold hover:bg-yellow-500 text-black font-bold py-2 px-6 rounded shadow-sm disabled:opacity-50 transition-colors" >
          {isLoading ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </div>
    </form>
  );
};

export default EditUserForm;